import { makeStyles, tokens } from "@fluentui/react-components";
// Constants
import { NavigationBarMinHeight, StandardButtonMinSize, TabListMinHeight } from "@ms/planner-generic-controls/lib/local/constants/StyleConstants";
// Utilities
import { flexParent } from "@ms/planner-generic-controls/lib/local/utilities/Mixin.styles";
// Styling constants
const sectionSpacingGroupSettingsToContent = tokens.spacingVerticalXXS;
/**
 * Hook that returns styles for the PlanSettingsPanel component
 */ export const usePlanSettingsPanelStyles = makeStyles({
    root: {
        ...flexParent("column"),
        height: "100%"
    },
    navigation: {
        ...flexParent("row"),
        alignItems: "center",
        justifyContent: "flex-start",
        padding: `${tokens.spacingVerticalM} ${tokens.spacingHorizontalM}`,
        backgroundColor: tokens.colorNeutralBackground1,
        minHeight: NavigationBarMinHeight,
        boxSizing: "border-box",
        width: "100%"
    },
    title: {
        fontSize: tokens.fontSizeBase400,
        fontWeight: tokens.fontWeightSemibold,
        color: tokens.colorNeutralForeground1,
        margin: "0"
    },
    closeButton: {
        marginLeft: "auto",
        minWidth: StandardButtonMinSize,
        minHeight: StandardButtonMinSize,
        padding: `2px 2px 0 ${tokens.spacingHorizontalXS}`,
        color: tokens.colorNeutralForeground2,
        ":hover": {
            backgroundColor: tokens.colorNeutralBackground1Hover,
            color: tokens.colorNeutralForeground1
        },
        ":active": {
            backgroundColor: tokens.colorNeutralBackground1Pressed
        }
    },
    drawerHeader: {
        paddingBottom: "0"
    },
    content: {
        ...flexParent("column"),
        flex: "1",
        height: "100%",
        width: "100%",
        boxSizing: "border-box",
        overflowX: "hidden",
        overflowY: "auto",
        paddingLeft: tokens.spacingHorizontalXL,
        paddingRight: tokens.spacingHorizontalXXL
    },
    pivots: {
        paddingTop: 0,
        paddingBottom: tokens.spacingVerticalXXL,
        paddingLeft: 0,
        paddingRight: 0,
        backgroundColor: tokens.colorNeutralBackground1
    },
    planTitleContainer: {
        paddingTop: 0,
        paddingBottom: tokens.spacingVerticalXXL,
        paddingLeft: 0,
        paddingRight: 0,
        backgroundColor: tokens.colorNeutralBackground1
    },
    planTitleInput: {
        width: "100%",
        fontSize: tokens.fontSizeBase500,
        fontWeight: tokens.fontWeightSemibold,
        "& > .fui-Input__input": {
            border: "none",
            borderBottom: `2px solid ${tokens.colorBrandBackground}`,
            borderRadius: "0",
            padding: `1px 0 1px ${tokens.spacingHorizontalM}`,
            backgroundColor: "transparent",
            fontSize: tokens.fontSizeBase500,
            fontWeight: tokens.fontWeightSemibold,
            outline: "none",
            ":focus": {
                borderBottom: `2px solid ${tokens.colorBrandBackground}`,
                outline: "none",
                boxShadow: "none"
            },
            ":hover": {
                borderBottom: `2px solid ${tokens.colorBrandBackground}`
            },
            "::placeholder": {
                color: tokens.colorNeutralForeground3
            }
        },
        // Ensure the outer container has no border
        border: "none",
        outline: "none",
        ":focus-within": {
            border: "none",
            outline: "none",
            boxShadow: "none"
        }
    },
    tabList: {
        minHeight: TabListMinHeight
    },
    settingsContent: {
        ...flexParent("column"),
        flex: "1",
        width: "100%",
        gap: tokens.spacingVerticalL,
        overflowX: "hidden",
        overflowY: "auto",
        boxSizing: "border-box",
        padding: 0,
        paddingRight: tokens.spacingHorizontalM
    },
    footer: {
        marginTop: "auto",
        paddingTop: tokens.spacingVerticalL,
        paddingBottom: tokens.spacingVerticalM,
        paddingLeft: tokens.spacingHorizontalM,
        paddingRight: tokens.spacingHorizontalM,
        display: "flex",
        gap: tokens.spacingHorizontalM,
        alignItems: "center"
    },
    saveButton: {
        borderRadius: tokens.borderRadiusXLarge,
        minWidth: "auto",
        fontWeight: tokens.fontWeightRegular
    },
    deleteButton: {
        borderRadius: tokens.borderRadiusXLarge,
        minWidth: "auto",
        fontWeight: tokens.fontWeightRegular
    },
    hiddenDescription: {
        position: "absolute",
        width: "1px",
        height: "1px",
        padding: 0,
        margin: "-1px",
        overflow: "hidden",
        clip: "rect(0, 0, 0, 0)",
        whiteSpace: "nowrap",
        border: 0
    },
    groupInputStyle: {
        borderRadius: tokens.borderRadiusXLarge,
        border: `1px solid ${tokens.colorNeutralStroke2}`,
        height: "auto",
        minHeight: "auto"
    },
    groupInputInnerStyle: {
        padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalS}`,
        color: tokens.colorNeutralForeground1,
        "::placeholder": {
            color: tokens.colorNeutralForeground4
        }
    },
    settingsContentContainer: {
        paddingTop: 0,
        paddingBottom: 0,
        paddingLeft: 0,
        paddingRight: 0,
        display: "flex",
        flexDirection: "column",
        gap: tokens.spacingVerticalM,
        flex: "1"
    },
    groupSettingsSection: {
    },
    groupSettingsToggleButton: {
        padding: `0 0 0 ${tokens.spacingHorizontalS}`,
        justifyContent: "flex-start",
        width: "100%",
        marginBottom: sectionSpacingGroupSettingsToContent,
        borderRadius: tokens.borderRadiusSmall
    },
    groupSettingsSectionTitle: {
        marginLeft: tokens.spacingHorizontalS,
        fontSize: tokens.fontSizeBase400,
        fontWeight: tokens.fontWeightSemibold
    },
    groupSettingsToggleIcon: {
        fontSize: "24px",
        fontWeight: tokens.fontWeightBold,
        "& svg": {
            strokeWidth: "1.5px"
        },
        "& path": {
            strokeWidth: "0.5",
            stroke: "currentColor"
        }
    },
    groupSettingsContent: {
        paddingInlineStart: tokens.spacingHorizontalXXL
    },
    groupSettingsLoadingContainer: {
        display: "flex",
        alignItems: "center",
        gap: tokens.spacingHorizontalS,
        padding: `${tokens.spacingVerticalM} 0`
    },
    groupSettingsLoadingText: {
        fontSize: tokens.fontSizeBase300
    },
    groupSettingsFieldsContainer: {
        display: "flex",
        flexDirection: "column",
        gap: tokens.spacingVerticalM
    },
    fieldLabel: {
        fontSize: tokens.fontSizeBase300,
        fontWeight: tokens.fontWeightSemibold
    },
    privacyField: {
        marginTop: tokens.spacingVerticalM
    },
    sensitivityLabelSection: {
        marginTop: tokens.spacingVerticalM
    },
    privacyDisabledNote: {
        fontSize: tokens.fontSizeBase200,
        color: tokens.colorNeutralForeground3,
        marginTop: tokens.spacingVerticalS
    },
    sensitivityLabelHeader: {
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        width: "100%"
    },
    sensitivityLearnMoreLink: {
        color: tokens.colorBrandForegroundLink,
        textDecoration: "underline",
        cursor: "pointer"
    },
    settingsContainerWrapper: {
        display: "flex",
        flexDirection: "column",
        height: "100%"
    }
});
/**
 * Styles for the TabList component in PlanSettingsPanel
 */ export const usePlanSettingsTabListStyles = makeStyles({
    root: {
        gap: tokens.spacingHorizontalS
    },
    tab: {
        borderRadius: tokens.borderRadiusCircular,
        padding: `${tokens.spacingVerticalXS} ${tokens.spacingHorizontalM}`,
        border: "none",
        backgroundColor: tokens.colorNeutralBackground3,
        color: tokens.colorNeutralForeground1,
        fontWeight: tokens.fontWeightRegular,
        ":hover": {
            backgroundColor: tokens.colorNeutralBackground3Hover
        },
        "&[aria-selected='true']": {
            backgroundColor: `${tokens.colorBrandBackground} !important`,
            color: `${tokens.colorNeutralForegroundOnBrand} !important`,
            ":hover": {
                backgroundColor: `${tokens.colorBrandBackgroundHover} !important`
            },
            // Ensure text content inherits white color
            "& *": {
                color: `${tokens.colorNeutralForegroundOnBrand} !important`
            }
        }
    }
});
